import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperienceContact,
  IExperienceTemplate
} from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import {
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { capitalize } from 'lodash';
import { TemplateMetaGrid } from '../../../../../01-standalone-app/features/Experience/components/TemplateMetaGrid';
import { CustomInputLabel } from '../../../../../components/CustomInputLabel';
import { FormRow } from '../../../../../components/Form/FormRow';
import { InfoCircle } from '../../../../../components/InfoCircle';
import { useTranslation } from '../../../../../i18next';
import { FlexContainer } from '../../../../../layout/Flex';
import { GridHalfSized } from '../../../../../layout/Grid';
import { useCurrentUser } from '../../../../../services/currentUser';
import { updateDataInDoc } from '../../../../../services/firebase';
import { ONE_DAY } from '../../../../../services/time';
import { Duration } from '../../../components/Builders/TaskBuilder/components/TimeSelectors';
import { StandardFooter } from '../Footer';
import { StepBodyProps } from '../types';

const updateTitle = (
  d: Doc<IExperienceTemplate>,
  title: string
): Doc<IExperienceTemplate> => {
  return updateDataInDoc(d, { title });
};

const updateAdminTitle = (
  d: Doc<IExperienceTemplate>,
  adminTitle: string
): Doc<IExperienceTemplate> => {
  return updateDataInDoc(d, { adminTitle });
};

const updateContacts = (
  d: Doc<IExperienceTemplate>,
  contacts: IExperienceContact[]
): Doc<IExperienceTemplate> => {
  return updateDataInDoc(d, { contacts });
};

const updateNotificationSenderName = (
  d: Doc<IExperienceTemplate>,
  senderName: string
): Doc<IExperienceTemplate> => {
  return updateDataInDoc(d, {
    notificationSender: Object.assign({}, d.data.notificationSender, {
      name: senderName
    })
  });
};
const updateNotificationSenderReplyTo = (
  d: Doc<IExperienceTemplate>,
  replyTo: string
): Doc<IExperienceTemplate> => {
  return updateDataInDoc(d, {
    notificationSender: Object.assign({}, d.data.notificationSender, {
      replyTo
    })
  });
};

const updateSso = (
  d: Doc<IExperienceTemplate>,
  sso: IExperienceTemplate['sso']
): Doc<IExperienceTemplate> => {
  return updateDataInDoc(d, { sso });
};

const ContactsRows = styled('div')`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 2fr 2fr;
  grid-column-gap: ${(p) => p.theme.spacing(1)}px;
  grid-row-gap: ${(p) => p.theme.spacing(2)}px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.spacing(2)}px;
  margin-bottom: ${(p) => p.theme.spacing(6)}px;
`;

const Contacts = ({
  value,
  onChange
}: {
  value: IExperienceContact[];
  onChange: (nextValue: IExperienceContact[]) => void;
}) => {
  const { t } = useTranslation();
  return (
    <>
      {value.map((contact, i) => (
        <ContactsRows key={i}>
          <Select disabled value={contact.type} variant="outlined">
            <MenuItem value="MAIL">{capitalize(t('email'))}</MenuItem>
          </Select>
          {contact.type === 'MAIL' && (
            <TextField
              label={capitalize(t('email'))}
              value={contact.email}
              variant="outlined"
              onChange={(ev) => {
                const email = ev.target.value;
                const nextContacts = [...value];
                nextContacts[i] = {
                  ...contact,
                  email
                };
                onChange(nextContacts);
              }}
            />
          )}
          <TextField
            label={`${capitalize(t('name'))} (${t('form.optional')})`}
            value={contact.name}
            variant="outlined"
            onChange={(ev) => {
              const name = ev.target.value;
              const nextContacts = [...value];
              nextContacts[i] = {
                ...contact,
                name
              };
              onChange(nextContacts);
            }}
          />
        </ContactsRows>
      ))}
    </>
  );
};

export const StepBasicsPreview = ({ d }: { d: Doc<IExperienceTemplate> }) => {
  return (
    <TemplateMetaGrid
      d={d}
      resourceTooltipMode="future"
      showTitle
      showAdminTitle
    />
  );
};

export const StepBasics = ({
  d,
  label,
  onChange,
  goTo,
  nextStep,
  onCheckChanges,
  onLaunchFromRoughDraft,
  isLaunchReady,
  onLaunch,
  lastSavedAt
}: StepBodyProps<Doc<IExperienceTemplate>>) => {
  const { t } = useTranslation();
  const { org } = useCurrentUser();

  const ssoPossible = org.sso.enabledForEndUsers;

  return (
    <>
      <GridHalfSized>
        <div>
          <Section>
            <Typography component="h1" variant="h3">
              {label}
            </Typography>
            <CustomInputLabel
              label={t('templateBuilder.basic.expTitle')}
              subLabel={t('templateBuilder.basic.expTitleHint')}
            >
              <TextField
                value={d.data.title}
                variant="outlined"
                fullWidth
                onChange={(ev) => {
                  const title = ev.target.value;
                  return onChange((x) => updateTitle(x, title));
                }}
              />
            </CustomInputLabel>
            <CustomInputLabel
              label={t('templateBuilder.basic.expAdminTitle')}
              subLabel={capitalize(
                t('templateBuilder.basic.expAdminTitleHint')
              )}
            >
              <TextField
                value={d.data.adminTitle}
                variant="outlined"
                fullWidth
                placeholder={t(
                  'templateBuilder.basic.expAdminTitlePlaceholder'
                )}
                onChange={(ev) => {
                  const adminTitle = ev.target.value;
                  return onChange((x) => updateAdminTitle(x, adminTitle));
                }}
              />
            </CustomInputLabel>
          </Section>
          <Section>
            <Typography component="h2" variant="h4">
              {t('templateBuilder.basic.contacts')}
            </Typography>

            <CustomInputLabel
              label={t('templateBuilder.basic.senderName')}
              subLabel={capitalize(t('visible to everyone'))}
            >
              <TextField
                value={d.data.notificationSender.name}
                helperText={`If empty, messages will be sent by "Dado"`}
                placeholder={t('templateBuilder.basic.senderNamePlaceholder')}
                variant="outlined"
                fullWidth
                onChange={(ev) =>
                  onChange((x) =>
                    updateNotificationSenderName(x, ev.target.value)
                  )
                }
              />
            </CustomInputLabel>
            <CustomInputLabel
              label={t('templateBuilder.basic.replyTo')}
              subLabel={capitalize(t('visible to everyone'))}
            >
              <TextField
                value={d.data.notificationSender.replyTo}
                placeholder={t('templateBuilder.basic.replyToPlaceholder')}
                variant="outlined"
                fullWidth
                onChange={(ev) =>
                  onChange((x) =>
                    updateNotificationSenderReplyTo(x, ev.target.value)
                  )
                }
              />
            </CustomInputLabel>
            <CustomInputLabel
              label={t('templateBuilder.basic.supportContact')}
              subLabel={capitalize(t('visible to everyone'))}
            >
              <Contacts
                value={d.data.contacts}
                onChange={(contacts) =>
                  onChange((x) => updateContacts(x, contacts))
                }
              />
            </CustomInputLabel>
          </Section>
          <Section>
            <Typography component="h2" variant="h4">
              {t('templateBuilder.basic.security')}
            </Typography>

            <FormControlLabel
              disabled={!ssoPossible}
              control={
                <Switch
                  disabled={!ssoPossible}
                  checked={!!d.data.sso && ssoPossible}
                  onChange={(ev) =>
                    onChange((x) =>
                      updateSso(
                        x,
                        x.data.sso
                          ? null
                          : { type: 'AFTER_FIRST_DAY_AT_WORK', offset: 0 }
                      )
                    )
                  }
                  color="primary"
                />
              }
              label={
                <Tooltip
                  title={`${t('templateBuilder.basic.ssoDisabledHint')}`}
                >
                  <FlexContainer>
                    <div>{t('templateBuilder.basic.requireSso')}</div>
                    {!ssoPossible && <InfoCircle />}
                  </FlexContainer>
                </Tooltip>
              }
            />

            {d.data.sso && ssoPossible && (
              <>
                <RadioGroup
                  name="sso"
                  value={
                    d.data.sso.type === 'ALWAYS'
                      ? 'ALWAYS'
                      : d.data.sso.offset === 0
                      ? 'ON_FIRST_DAY'
                      : 'AFTER_FIRST_DAY'
                  }
                  onChange={(ev) => {
                    const v = ev.target.value;
                    onChange((x) =>
                      updateSso(
                        x,
                        v === 'ALWAYS'
                          ? { type: 'ALWAYS' }
                          : v === 'ON_FIRST_DAY'
                          ? { type: 'AFTER_FIRST_DAY_AT_WORK', offset: 0 }
                          : {
                              type: 'AFTER_FIRST_DAY_AT_WORK',
                              offset: ONE_DAY
                            }
                      )
                    );
                  }}
                >
                  <FlexContainer spacing={2}>
                    <FormControlLabel
                      value={'ON_FIRST_DAY'}
                      control={<Radio color="primary" />}
                      label={t(
                        'templateBuilder.basic.ssoOptions.onFirstDayAtWork'
                      )}
                    />
                    <FormControlLabel
                      value={'AFTER_FIRST_DAY'}
                      control={<Radio color="primary" />}
                      label={t(
                        'templateBuilder.basic.ssoOptions.afterFirstDayAtWork'
                      )}
                    />
                    <FormControlLabel
                      value={'ALWAYS'}
                      control={<Radio color="primary" />}
                      label={t('templateBuilder.basic.ssoOptions.always')}
                    />
                  </FlexContainer>
                </RadioGroup>
                {d.data.sso.type === 'AFTER_FIRST_DAY_AT_WORK' &&
                  d.data.sso.offset > 0 && (
                    <FormRow>
                      <FlexContainer>
                        <div>
                          {t(
                            'templateBuilder.basic.ssoOptions.afterFirstDayAtWorkOffsetLabel'
                          )}
                        </div>
                        <Duration
                          value={d.data.sso.offset}
                          onChange={(nextValue) => {
                            onChange((x) =>
                              updateSso(x, {
                                type: 'AFTER_FIRST_DAY_AT_WORK',
                                offset: nextValue
                              })
                            );
                          }}
                        />
                      </FlexContainer>
                    </FormRow>
                  )}
              </>
            )}
          </Section>
        </div>
      </GridHalfSized>

      <StandardFooter d={d} onCheckChanges={onCheckChanges} onLaunchFromRoughDraft={onLaunchFromRoughDraft} isLaunchReady={isLaunchReady} onLaunch={onLaunch} lastSavedAt={lastSavedAt} />
    </>
  );
};
